import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, ArrowRight, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#22D3EE";
const ACCENT_DEEP = "#0891B2";


/**
 * Bundle access page — landing target after Cashfree redirects back from a bundle payment.
 * Polls /api/courses/bundles/verify until the order shows PAID, then renders one card
 * per enrolled course with its own "Start learning" button (magic-token link).
 */
export default function BundleAccessPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [data, setData] = useState({ courses: [], failed_slugs: [] });
  const [tries, setTries] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!orderId) {
      setStatus("error"); setErr("Missing order_id in URL"); return;
    }
    let cancelled = false;

    async function check(attempt) {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/courses/bundles/verify?order_id=${orderId}`);
        if (cancelled) return;
        if (res.data.status === "paid") {
          setData(res.data);
          setStatus("paid");
        } else if (attempt < 12) {
          setTries(attempt + 1);
          setTimeout(() => check(attempt + 1), 4000);
        } else {
          setStatus("pending");
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || "Could not verify payment.");
        setStatus("error");
      }
    }
    check(0);
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4">
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-8 max-w-xl w-full" data-testid="bundle-access-page">
        {status === "checking" && (
          <div className="text-center">
            <Loader2 size={36} className="animate-spin mx-auto text-cyan-400 mb-4" />
            <p className="text-white text-lg font-bold mb-1">Confirming your bundle payment…</p>
            <p className="text-zinc-400 text-sm">This usually takes 5–15 seconds. Don't close this page. (attempt {tries + 1}/13)</p>
          </div>
        )}

        {status === "paid" && (
          <>
            <div className="text-center mb-6">
              <CheckCircle2 size={44} className="mx-auto text-emerald-400 mb-3" />
              <p className="text-white text-xl font-extrabold">Bundle activated 🎉</p>
              <p className="text-zinc-400 text-sm mt-1">
                {data.courses.length} courses unlocked · we've also emailed you these access links so you can pick up any time.
              </p>
            </div>
            <div className="space-y-2 mb-4">
              {data.courses.map((c) => (
                <Link
                  key={c.slug}
                  to={`/learn/${c.slug}?token=${encodeURIComponent(c.magic_token)}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 hover:border-cyan-500/50 transition-all p-3 group"
                  data-testid={`bundle-access-course-${c.slug}`}
                >
                  <div className="min-w-0">
                    <p className="text-white text-sm font-bold leading-tight truncate">{c.slug.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</p>
                    <p className="text-zinc-500 text-xs">Tap to start learning →</p>
                  </div>
                  <span
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-black text-xs font-bold"
                    style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                  >
                    Start <ArrowRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
            {data.failed_slugs && data.failed_slugs.length > 0 && (
              <p className="text-amber-300 text-xs bg-amber-900/20 border border-amber-500/30 rounded-lg p-2.5">
                <Sparkles size={12} className="inline mr-1" />
                Heads-up: {data.failed_slugs.length} course(s) hit a small hiccup activating. Our team has been alerted and will activate them within a few hours. You'll get an email once they're ready.
              </p>
            )}
            <p className="text-zinc-500 text-xs text-center mt-4">
              Need anything? Email <span className="text-cyan-300">support@shansoai.com</span>
            </p>
          </>
        )}

        {status === "pending" && (
          <div className="text-center">
            <AlertCircle size={36} className="mx-auto text-amber-400 mb-4" />
            <p className="text-white text-lg font-bold mb-1">Payment is taking a little longer than usual</p>
            <p className="text-zinc-400 text-sm mb-4">If you've completed payment, please check back in a minute or refresh this page. If money was deducted but the bundle didn't activate, email support@shansoai.com with order ID <code className="text-cyan-300">{orderId}</code> and we'll fix it manually.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2.5 rounded-lg text-black font-bold text-sm"
              style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
              data-testid="bundle-access-retry"
            >
              Check again
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="text-center">
            <AlertCircle size={36} className="mx-auto text-red-400 mb-4" />
            <p className="text-white text-lg font-bold mb-1">We couldn't verify your payment</p>
            <p className="text-zinc-400 text-sm mb-4">{err}</p>
            <Link
              to="/courses"
              className="inline-flex items-center gap-1 px-4 py-2.5 rounded-lg text-black font-bold text-sm"
              style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
            >
              Back to courses
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
